import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
} from '@mui/material';

const TIER_COLORS = {
  basic: 'default',
  pro: 'primary',
  enterprise: 'secondary',
};

CurrentPlanCard.propTypes = {
  subscription: PropTypes.shape({
    planId: PropTypes.string,
    planName: PropTypes.string,
    tier: PropTypes.string,
    priceEur: PropTypes.number,
    unlockedFeatures: PropTypes.arrayOf(PropTypes.string),
    supportTier: PropTypes.string,
    freeCreditsIncluded: PropTypes.number,
    pendingPlanId: PropTypes.string,
  }).isRequired,
};

export default function CurrentPlanCard({ subscription }) {
  const { t } = useTranslation();
  const {
    planId,
    planName,
    tier,
    priceEur,
    unlockedFeatures = [],
    supportTier,
    freeCreditsIncluded = 0,
    pendingPlanId,
  } = subscription;

  const priceLabel = priceEur != null
    ? t('credits.pricePerMonth', { price: priceEur })
    : t('credits.priceCustom');

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="overline" color="text.secondary">
              {t('credits.currentPlan')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="h5">{planName || planId}</Typography>
              {tier && (
                <Chip
                  label={t(`credits.tier.${tier}`, { defaultValue: tier })}
                  size="small"
                  color={TIER_COLORS[tier] || 'default'}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {priceLabel}
            </Typography>
            {pendingPlanId && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                {t('credits.pendingPlanNotice', { planId: pendingPlanId })}
              </Typography>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ flex: 1.5, minWidth: 260 }}>
            <Typography variant="overline" color="text.secondary">
              {t('credits.unlockedFeatures')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {unlockedFeatures.length > 0 ? (
                unlockedFeatures.map((f) => (
                  <Chip
                    key={f}
                    label={t(`credits.unlocked.${f}`, { defaultValue: f })}
                    size="small"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('credits.noUnlockedFeatures')}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
              {freeCreditsIncluded > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('credits.freeCreditsIncluded')}
                  </Typography>
                  <Typography variant="body2">{freeCreditsIncluded}</Typography>
                </Box>
              )}
              {supportTier && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('credits.support')}
                  </Typography>
                  <Typography variant="body2">
                    {t(`credits.supportTier.${supportTier}`, { defaultValue: supportTier })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
